import React, { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  CurrentLocation,
  HealthStatusBadge,
} from "../../../components/ui/dashboard/StatusCommon";
import Table, { Column } from "../../../components/ui/dashboard/Table";
import { formatCurrencyForTable } from "../../../lib/utils";
import UserCard from "../../user/ui/UserCard";
import {
  Vehicle,
  VehicleSearchRequest,
  VehicleSearchResponse,
} from "../../vehicle/@types";
import { useGetPartnerVehiclesMutation } from "../../vehicle/vehicleApiSlice";

interface PartnerVehiclesTableProps {
  partnerId: string;
}

const PartnerVehiclesTable: React.FC<PartnerVehiclesTableProps> = ({
  partnerId,
}) => {
  const navigate = useNavigate();

  // RTK Query mutation
  const [getVehicles, { data: vehicleData, isLoading }] =
    useGetPartnerVehiclesMutation();

  const fetchVehicles = useCallback(async () => {
    const requestBody: VehicleSearchRequest = {
      pageNumber: 0,
      pageSize: 100,
      partnerId: partnerId,
    };

    try {
      await getVehicles(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch vehicles:", err);
    }
  }, [getVehicles]);

  // Fetch vehicles on component mount and when filters change
  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  // Table columns configuration
  const columns: Column<Vehicle>[] = [
    {
      key: "vehicleName",
      header: "Vehicle name/model",
      width: "20",
      render: (value: string, item: Vehicle) => (
        <span className="text-hColor">{value + " " + item.vehicleModel}</span>
      ),
    },
    {
      key: "vehicleType",
      header: "Vehicle type",
      width: "24",
      render: (value: string, item) => (
        <span className="text-hColor capitalize">{value}</span>
      ),
    },
    {
      key: "currentDriver",
      header: "Assigned driver",
      width: "20",
      render: (value: string, item) => (
        <UserCard fullName={item.currentDriver} imageUrl={item.driverImg} />
      ),
    },
    {
      key: "totalIncome",
      header: "Total earning (₦)",
      width: "16",
      render: (value: number) => (
        <span className="text-hColor">{formatCurrencyForTable(value)}</span>
      ),
    },
    {
      key: "totalExpenses",
      header: "Total expenses (₦)",
      width: "16",
      render: (value: number) => (
        <span className="text-hColor">{formatCurrencyForTable(value)}</span>
      ),
    },
    {
      key: "totalTrips",
      header: "Total trips",
      width: "16",
      render: (value: number) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "currentLocation",
      header: "Current location",

      render: (value: string) => <CurrentLocation location={value} />,
    },
    {
      key: "healthStatus",
      header: "Health status",
      width: "20",
      render: (value: string) => <HealthStatusBadge healthStatus={value} />,
    },
    {
      key: "actions",
      header: "Action",
      width: "12",
      align: "center" as const,
      render: (value: any, item: Vehicle) => (
        <span
          onClick={() => navigate(`/admin/vehicles/${item?.id}`)}
          className="text-primary"
        >
          View
        </span>
      ),
    },
  ];

  const response = vehicleData?.data as VehicleSearchResponse | undefined;

  return (
    <div className="">
      <Table
        data={response?.data || []}
        columns={columns}
        loading={isLoading}
        emptyMessage="No vehicles found"
      />
    </div>
  );
};

export default PartnerVehiclesTable;
